import api from './api';
import { Customer, LoginCredentials, SignupData, ApiResponse } from '../types';

class AuthService {
  async login(credentials: LoginCredentials): Promise<Customer> {
    const response = await api.post<ApiResponse<Customer>>('/auth/customer/login', {
      email: credentials.email,
      password: credentials.password,
    });
    
    // Store CSRF token from response in sessionStorage for cross-domain support
    const data = response.data.data;
    if ((data as any).csrfToken) {
      sessionStorage.setItem('csrfToken', (data as any).csrfToken);
    }
    
    return data;
  }

  async logout(): Promise<void> {
    await api.post('/auth/customer/logout');
    // Clear CSRF token from sessionStorage
    sessionStorage.removeItem('csrfToken');
  }

  async signup(data: SignupData): Promise<{ id: string; email: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; email: string; message: string }>>(
      '/auth/customer/signup',
      data
    );
    return response.data.data;
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      '/auth/customer/verify-email',
      { token }
    );
    return response.data.data;
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      '/auth/customer/resend-verification',
      { email }
    );
    return response.data.data;
  }

  async getCurrentUser(): Promise<Customer> {
    const response = await api.get<ApiResponse<Customer>>('/auth/customer/me');
    return response.data.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      '/auth/customer/forgot-password',
      { email }
    );
    return response.data.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      '/auth/customer/reset-password',
      { token, newPassword }
    );
    return response.data.data;
  }

  async refreshCSRFToken(): Promise<string> {
    const response = await api.get<ApiResponse<{ csrfToken: string; message: string }>>('/auth/customer/csrf-token');
    const csrfToken = response.data.data.csrfToken;
    // Store in sessionStorage for cross-domain support
    if (csrfToken) {
      sessionStorage.setItem('csrfToken', csrfToken);
    }
    return csrfToken;
  }
}

export default new AuthService();